import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@maestra/core/store/store';
import { artistsActions } from '@maestra/core/store/slices/artists';
import { uiActions } from '@maestra/core/store/slices/ui';
import { fetchSubscriptionStatus, fetchPlanConfig } from '@maestra/core/store/slices/subscription';
import { PAYWALL_DISABLED, artistEntryRoute, isOnboardingComplete } from '@maestra/core/constants/maestra';
import { perfilEstaAtivo } from '@maestra/core/hooks/useArtistCapabilities';
import { useEntitlements } from '@maestra/core/hooks/useEntitlements';
import { useGlobalSearch } from '@maestra/core/stores/globalSearchStore';
import { useNytaModal } from '@maestra/core/hooks/useNytaModal';
import { useLocalPlayerStore } from '@maestra/core/stores/localPlayerStore';
import { countUnread } from '@maestra/core/services/db/notifications';
import { supabase } from '@maestra/core/lib/supabase';
import { useArtistaAoVivo, useVagaNaEquipe } from '@maestra/core/hooks/useAoVivo';
import { AVISOS_DA_EQUIPE } from '@maestra/core/constants/equipeTextos';
import { estadoDaCota } from '@maestra/core/nucleo/cotaDeArmazenamento';
import { fotoDoArtista } from '@maestra/core/nucleo/fotoDoArtista';

import { enableWebPush, hasWebPushSubscription, isWebPushSupported, syncWebPushSubscription } from '../../services/pushNotifications';
import useIsMobile from '../../utils/isMobile';
import { message } from '../../ui-carbon/padroes/avisos';
import { useAbrirNyta } from './useAbrirNyta';

// A LÓGICA DA CASCA, fora do desenho.
//
// Veio inteira do `AppLayout` antigo (components/Layout), na mesma ordem e com os mesmos efeitos:
// o contador de não-lidas com realtime, o pedido de push, o status da assinatura e os preços, a
// inscrição ao vivo do perfil aberto, a saída de quem foi removido da equipe, a busca da tela, o
// player global e a escolha de para onde cada perfil abre. A casca do Carbon (ui-carbon/casca) só
// lê daqui. Nada disto é desenho, e nada disto mudou.

const perfilDaRota = (pathname: string): string | undefined =>
  /^\/artists\/([^/]+)/.exec(pathname)?.[1];

// Rotas antigas que ainda filtram pelo campo do topo (globalSearchStore). A tela nova tem a busca
// na própria barra (a do DataTable ou a da lista); ao migrar uma tela, a rota sai daqui.
// Músicas tem a busca na barra da própria tabela; a lista de perfis busca pelo topo (não há outra
// barra ali).
const ROTAS_COM_BUSCA_NO_TOPO = /^\/artists(\/[^/]+\/agenda)?$/;

export const useCasca = () => {
  // O painel de perfis abre perfis, e para onde um perfil abre depende de ele estar ativo.
  const { isPro } = useEntitlements();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { isOpen: nytaOpen, close: fecharNyta } = useNytaModal();
  const abrirNyta = useAbrirNyta();
  const termoBusca = useGlobalSearch((s) => s.termo);
  const setTermoBusca = useGlobalSearch((s) => s.setTermo);
  const limparBusca = useGlobalSearch((s) => s.limpar);

  // Cada tela filtra o seu; carregar o termo de uma para outra esconderia itens sem motivo.
  useEffect(() => { limparBusca(); }, [location.pathname, limparBusca]);

  // A casca permanece montada entre as rotas: sem isto a tela nova herdava a rolagem da anterior.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname, location.search]);

  const playerOpen = useLocalPlayerStore((s) => s.open);
  const playerTracks = useLocalPlayerStore((s) => s.tracks);
  const playerCurrentId = useLocalPlayerStore((s) => s.currentId);
  const setPlayerOpen = useLocalPlayerStore((s) => s.setOpen);
  const setPlayerCurrentId = useLocalPlayerStore((s) => s.setCurrentId);
  // A lista global de perfis não tem contexto de reprodução; no celular a Nyta ocupa a tela.
  const isArtistsList = location.pathname.replace(/\/+$/, '') === '/artists';
  const playerHidden = isArtistsList || (isMobile && nytaOpen);
  const playerVisible = playerOpen && !playerHidden;
  const userId = useAppSelector((s) => s.auth.user?.id);

  // Não-lidas no sino: recarrega ao trocar de rota (o aviso some depois de abrir /notifications)
  // e escuta realtime, para acender sem precisar navegar.
  const [naoLidas, setNaoLidas] = useState(0);
  useEffect(() => {
    if (!userId) { setNaoLidas(0); return; }
    let vivo = true;
    const recontar = () => countUnread(userId).then((n) => { if (vivo) setNaoLidas(n); }).catch(() => {});
    recontar();
    const canal = supabase
      .channel(`notifications-badge:${userId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` },
        recontar,
      )
      .subscribe();
    return () => { vivo = false; void supabase.removeChannel(canal); };
  }, [userId, location.pathname]);

  const artists = useAppSelector((s) => s.artists.items);
  const routeArtistId = perfilDaRota(location.pathname);
  const currentArtist = routeArtistId ? artists.find((artist) => artist.id === routeArtistId) : undefined;
  const isNotificationsPage = location.pathname === '/notifications';
  // O perfil aberto, ao vivo: UMA inscrição, aqui na casca, para todas as telas do perfil.
  useArtistaAoVivo(currentArtist?.id);
  // Removido da equipe com o perfil aberto: sai dele na hora, em vez de gravar no vazio.
  useVagaNaEquipe(currentArtist?.id, userId, currentArtist?.role === 'member', () => {
    message.warning(AVISOS_DA_EQUIPE.aviso_saiu_da_equipe);
    if (userId) dispatch(artistsActions.fetchArtists(userId));
    navigate('/artists');
  }, (niveis) => {
    // O titular mudou o acesso com o perfil aberto: recarrega os perfis, e o portão de cada
    // módulo (e a caneta) passa a valer na hora, sem precisar recarregar a página.
    const antes = JSON.stringify([...(currentArtist?.access_levels ?? [])].sort());
    if (userId && JSON.stringify([...niveis].sort()) !== antes) {
      message.info(AVISOS_DA_EQUIPE.aviso_acesso_mudou);
      dispatch(artistsActions.fetchArtists(userId));
    }
  });

  // Push na primeira entrada autenticada. O navegador pode bloquear sem gesto; Configurações é o
  // caminho manual.
  useEffect(() => {
    if (!userId || !isWebPushSupported()) return undefined;
    const timer = window.setTimeout(() => {
      syncWebPushSubscription()
        .then((synced) => {
          if (synced || Notification.permission !== 'default') return undefined;
          const promptKey = `maestra-push-prompted:${userId}`;
          if (localStorage.getItem(promptKey)) return undefined;
          localStorage.setItem(promptKey, '1');
          return hasWebPushSubscription().then((enabled) => {
            if (!enabled) return enableWebPush().catch(() => undefined);
            return undefined;
          });
        })
        .catch(() => undefined);
    }, 600);
    return () => window.clearTimeout(timer);
  }, [userId]);

  const currentArtistId = useAppSelector((s) => s.artists.currentArtistId);
  // A Nyta só entra em cena depois do planejamento concluído: sem plano ela não tem sobre o que
  // conversar, e a porta de entrada levaria a um beco.
  const nytaAvailable = !!currentArtist && isOnboardingComplete(currentArtist);

  // Entrar numa das exceções fecha o player (desmontar o áudio para a reprodução).
  useEffect(() => {
    if (playerOpen && playerHidden) {
      setPlayerOpen(false);
      setPlayerCurrentId(null);
    }
  }, [playerHidden, playerOpen, setPlayerCurrentId, setPlayerOpen]);

  // O status da assinatura, uma vez ao autenticar, para o app inteiro.
  useEffect(() => {
    if (userId && !PAYWALL_DISABLED) {
      dispatch(fetchSubscriptionStatus());
    }
  }, [userId, dispatch]);

  // Preços do produto (config dinâmica) para os upsells e paywalls.
  useEffect(() => {
    dispatch(fetchPlanConfig());
  }, [dispatch]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth < 950) dispatch(uiActions.collapseLibrary());
      else dispatch(uiActions.openLibrary());
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [dispatch]);

  const moduloAtivo = (suffix: string) => {
    if (!routeArtistId) return false;
    if (suffix === '') return location.pathname === `/artists/${routeArtistId}`;
    return location.pathname.startsWith(`/artists/${routeArtistId}/${suffix}`);
  };

  const abrirModulo = (suffix: string) => {
    if (!routeArtistId) return;
    navigate(`/artists/${routeArtistId}${suffix ? `/${suffix}` : ''}`);
  };

  // A busca do topo só aparece onde filtra alguma coisa (campo que não responde é pior que campo
  // ausente), e o texto diz o que ela alcança ali.
  const rota = location.pathname.replace(/\/+$/, '');
  const buscaFiltraAqui = ROTAS_COM_BUSCA_NO_TOPO.test(rota);
  const buscaPlaceholder = (() => {
    if (rota.endsWith('/agenda')) return 'Buscar compromisso';
    if (rota === '/artists') return 'Buscar perfil ou artista';
    return 'Pesquisar';
  })();

  const comNavegacao = !isArtistsList && !!currentArtist && !isNotificationsPage;

  // O PERFIL ABERTO, no alto do menu (quem é, e em que perfil REAL está), e o plano e o espaço da
  // conta, no pé dele. O espaço é da conta: quem é da equipe não o vê (a mesma regra de Arquivos e
  // do Início), e quem é da equipe não assina por aquele perfil.
  const statusCarregado = useAppSelector((s) => s.subscription.initialized);
  const cotaEmBytes = useAppSelector((s) => s.subscription.storageQuotaBytes);
  const usadoEmBytes = useAppSelector((s) => s.subscription.storageUsedBytes);
  const membro = currentArtist?.role === 'member';
  const perfilAtual = currentArtist ? {
    id: currentArtist.id,
    nome: currentArtist.name,
    // Sem foto do Spotify, nada: a casca mostra as iniciais (a imagem padrão era igual para todos).
    imagem: fotoDoArtista(currentArtist),
    apoio: membro
      ? 'Você está na equipe deste perfil'
      : currentArtist.content?.realIndex?.profile?.name ? `Perfil REAL: ${currentArtist.content.realIndex.profile.name}` : 'Perfil de artista',
    membro,
  } : null;
  const espaco = currentArtist && !membro && statusCarregado ? estadoDaCota(usadoEmBytes, cotaEmBytes) : null;
  const semCota = cotaEmBytes !== Infinity && !(cotaEmBytes > 0);

  const perfis = artists.map((a) => ({
    id: a.id,
    nome: a.name,
    imagem: fotoDoArtista(a),
  }));

  return {
    rota: location.pathname,
    abrirPerfis: () => navigate('/artists'),
    busca: buscaFiltraAqui
      ? { termo: termoBusca, placeholder: buscaPlaceholder, aoMudar: setTermoBusca, aoLimpar: limparBusca }
      : undefined,
    abrirNyta: routeArtistId && nytaAvailable ? () => abrirNyta() : undefined,
    // A Nyta é um painel da direita da casca: abrir outro painel a fecha (um de cada vez).
    nytaAberta: nytaOpen,
    fecharNyta,
    perfilAtual,
    // No plano grátis a cota é zero: "Sem espaço", em vermelho, era a primeira coisa que um perfil
    // novo via, como se algo estivesse errado (pedido do Lucas, 04/10/2026). Fica neutro; o
    // vermelho é de quem assina e lotou o espaço de verdade.
    espaco: espaco ? (semCota
      ? { rotulo: 'O espaço para arquivos vem com o Maestra PRO.', percentual: 0, cheio: false, semCota: true }
      : { rotulo: espaco.rotulo, percentual: espaco.percentual, cheio: espaco.nivel === 'cheio', semCota: false }) : null,
    naoLidas,
    // O sino abre o painel das notificações; a página inteira continua em /notifications.
    verTodasAsNotificacoes: () => navigate('/notifications'),
    perfis,
    abrirPerfil: (id: string) => {
      const artist = artists.find((a) => a.id === id);
      if (artist) navigate(artistEntryRoute(artist, { perfilAtivo: perfilEstaAtivo(artist, isPro) }));
    },
    // Vai por /artists?create=1: aquela tela é quem checa o limite de perfis e avisa o motivo.
    criarPerfil: () => navigate('/artists?create=1'),
    comNavegacao,
    moduloAtivo,
    abrirModulo,
    player: playerVisible && playerCurrentId && playerTracks.length > 0
      ? {
          faixas: playerTracks,
          atual: playerCurrentId,
          trocar: setPlayerCurrentId,
          // A faixa diz para onde volta (o áudio aberto por Arquivos volta para a pasta dele);
          // sem isso, Músicas.
          abrirFaixa: () => {
            const atual = playerTracks.find((t) => t.id === playerCurrentId);
            if (atual?.href) { navigate(atual.href); return; }
            if (currentArtistId) {
              navigate(`/artists/${currentArtistId}/catalog`, { state: { catalogTab: 'manual' } });
            }
          },
          abrirVisualizacao: (url: string) => navigate(url),
          fechar: () => {
            setPlayerOpen(false);
            setPlayerCurrentId(null);
          },
        }
      : undefined,
  };
};

export type Casca = ReturnType<typeof useCasca>;
